import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tabs,
  Button,
  Card,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Spin,
  Empty,
  Avatar,
  Tooltip,
  Popconfirm,
  Space,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  DeleteOutlined,
  SendOutlined,
  LinkOutlined,
  BankOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 {
    font-size: 24px;
    font-weight: 600;
    color: #014c75;
    margin: 0 0 8px 0;
  }
  p {
    color: #666;
    margin: 0;
    font-size: 14px;
  }
`;

const MyPartnersWrapper = styled.div``;

const PartnerCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  .partner-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  .partner-name {
    font-size: 16px;
    font-weight: 600;
    color: #014c75;
    margin: 0;
  }
  .partner-org {
    font-size: 13px;
    color: #666;
    margin: 2px 0 0;
  }
  .partner-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #555;
  }
  .partner-meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .partner-stats {
    display: flex;
    gap: 0;
    border-top: 1px solid #f0f0f0;
    padding-top: 12px;
    margin-top: 4px;
  }
  .stat-item {
    flex: 1;
    text-align: center;
    border-right: 1px solid #f0f0f0;
    &:last-child {
      border-right: none;
    }
  }
  .stat-value {
    font-size: 18px;
    font-weight: 700;
    color: #014c75;
  }
  .stat-label {
    font-size: 11px;
    color: #999;
    margin-top: 2px;
  }
  .partner-actions {
    margin-top: 12px;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
`;

const AddPartnerModal = ({ visible, onClose, onSuccess }) => {
  const [mode, setMode] = useState("add");
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    form.resetFields();
    setMode("add");
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const token = localStorage.getItem("authToken");

      if (mode === "add") {
        await axios.post(
          API_CONST.PARTNER_RELATION_ADD,
          {
            partnerName: values.partnerName,
            partnerEmail: values.partnerEmail,
            partnerContact: values.partnerContact,
            partnerPhone: values.partnerPhone,
            partnerWebsite: values.partnerWebsite,
            notes: values.notes,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Partner added successfully");
      } else {
        await axios.post(
          API_CONST.PARTNER_RELATION_INVITE,
          {
            partnerEmail: values.inviteEmail,
            partnerName: values.inviteName?.trim() || undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Invitation sent to " + values.inviteEmail);
      }

      onSuccess();
      handleClose();
    } catch (error) {
      if (error.response) {
        message.error(error.response.data?.message || "Operation failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Add Partner"
      open={visible}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={mode === "add" ? "Add Partner" : "Send Invitation"}
      okButtonProps={{
        loading: submitting,
        style: { background: "#00d9a9", borderColor: "#00d9a9" },
      }}
      width={560}
      destroyOnClose
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <Button
          type={mode === "add" ? "primary" : "default"}
          onClick={() => { setMode("add"); form.resetFields(); }}
          icon={<BankOutlined />}
          style={mode === "add" ? { background: "#014c75", borderColor: "#014c75" } : {}}
        >
          Enter Details
        </Button>
        <Button
          type={mode === "invite" ? "primary" : "default"}
          onClick={() => { setMode("invite"); form.resetFields(); }}
          icon={<MailOutlined />}
          style={mode === "invite" ? { background: "#014c75", borderColor: "#014c75" } : {}}
        >
          Invite by Email
        </Button>
      </div>

      {mode === "add" ? (
        <Form form={form} layout="vertical">
          <Form.Item
            name="partnerName"
            label="Partner / Company Name"
            rules={[{ required: true, message: "Partner name is required" }]}
          >
            <Input placeholder="e.g. Acme Tech Solutions" prefix={<BankOutlined />} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="partnerEmail"
                label="Email"
                rules={[{ type: "email", message: "Enter a valid email" }]}
              >
                <Input placeholder="contact@partner.com" prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="partnerContact" label="Contact Person">
                <Input placeholder="Contact name" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="partnerPhone" label="Phone">
                <Input placeholder="+91 9876543210" prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="partnerWebsite" label="Website">
                <Input placeholder="https://partner.com" prefix={<LinkOutlined />} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes about this partner" />
          </Form.Item>
        </Form>
      ) : (
        <Form form={form} layout="vertical">
          <p style={{ color: "#666", marginBottom: 16, fontSize: 13 }}>
            Enter the partner's email address to send them a platform invitation link.
          </p>
          <Form.Item
            name="inviteEmail"
            label="Partner Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="partner@company.com" prefix={<MailOutlined />} size="large" />
          </Form.Item>
          <Form.Item name="inviteName" label="Partner / Company Name (optional)">
            <Input placeholder="e.g. Acme Tech Solutions" prefix={<BankOutlined />} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

const MyPartners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsMap, setStatsMap] = useState({});
  const [activeTab, setActiveTab] = useState("active");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.PARTNER_RELATION_MY_PARTNERS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartners(response.data.relations || []);
    } catch (error) {
      console.error("Error fetching partners:", error);
      message.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStatsForPartner = useCallback(async (relationId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.PARTNER_RELATION_PARTNER_STATS}/${relationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatsMap((prev) => ({
        ...prev,
        [relationId]: response.data.stats || {},
      }));
    } catch (error) {
      console.error("Error fetching partner stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  useEffect(() => {
    const activePartners = partners.filter((p) => p.status === "active" && p.partnerOrganization);
    activePartners.forEach((p) => {
      if (!statsMap[p.id]) {
        fetchStatsForPartner(p.id);
      }
    });
  }, [partners, fetchStatsForPartner, statsMap]);

  const handleRemove = async (partner) => {
    setRemovingId(partner.id);
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`${API_CONST.PARTNER_RELATION_REMOVE}/${partner.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success("Partner removed successfully");
      fetchPartners();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to remove partner");
    } finally {
      setRemovingId(null);
    }
  };

  const handleResendInvite = async (partner) => {
    setResendingId(partner.id);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.PARTNER_RELATION_RESEND_INVITE}/${partner.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Invitation resent successfully");
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to resend invitation");
    } finally {
      setResendingId(null);
    }
  };

  const handleCancelInvite = async (partner) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(
        `${API_CONST.PARTNER_RELATION_CANCEL_INVITE}/${partner.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Invitation cancelled");
      fetchPartners();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to cancel invitation");
    }
  };

  const activePartners = partners.filter((p) => p.status === "active");
  const invitedPartners = partners.filter((p) => p.status === "pending_invite");

  const getStatusBadge = (partner) => {
    if (partner.status === "active") {
      return partner.partnerOrganization ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>On Platform</Tag>
      ) : (
        <Tag color="cyan" icon={<CheckCircleOutlined />}>Offline</Tag>
      );
    }
    if (partner.status === "pending_invite") {
      return <Tag color="orange" icon={<ClockCircleOutlined />}>Pending Invite</Tag>;
    }
    return <Tag>{partner.status}</Tag>;
  };

  const renderActivePartnerCard = (partner) => {
    const stats = statsMap[partner.id] || {};
    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={partner.id}>
        <PartnerCard
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/customer/my-partners/${partner.id}`)}
        >
          <div className="partner-header">
            <Avatar size={44} icon={<BankOutlined />} style={{ background: "#e6f7ff", color: "#014c75" }} />
            <div>
              <p className="partner-name">
                {partner.partnerOrganization || partner.invitedName || "Offline Partner"}
              </p>
              <p className="partner-org">
                {partner.invitedName && partner.partnerOrganization ? partner.invitedName : ""}
              </p>
            </div>
            <div style={{ marginLeft: "auto" }}>{getStatusBadge(partner)}</div>
          </div>

          <div className="partner-meta">
            {partner.invitedEmail && (
              <span className="partner-meta-item">
                <MailOutlined style={{ color: "#999" }} />
                {partner.invitedEmail}
              </span>
            )}
            {partner.invitedContact && (
              <span className="partner-meta-item">
                <PhoneOutlined style={{ color: "#999" }} />
                {partner.invitedContact}
              </span>
            )}
            {partner.acceptedAt && (
              <span className="partner-meta-item">
                <CheckCircleOutlined style={{ color: "#999" }} />
                Added {new Date(partner.acceptedAt).toLocaleDateString("en-US", {
                  day: "2-digit", month: "short", year: "numeric",
                })}
              </span>
            )}
            <span className="partner-meta-item">
              {partner.source === "referral" ? (
                <Tag icon={<GiftOutlined />} color="purple" style={{ margin: 0 }}>Referral</Tag>
              ) : partner.source === "invite" ? (
                <Tag icon={<MailOutlined />} color="geekblue" style={{ margin: 0 }}>Invite</Tag>
              ) : partner.source === "bench_share" ? (
                <Tag icon={<ShareAltOutlined />} color="cyan" style={{ margin: 0 }}>Bench Share</Tag>
              ) : (
                <Tag color="default" style={{ margin: 0 }}>Manual</Tag>
              )}
            </span>
          </div>

          {partner.partnerOrganization && (
            <div className="partner-stats">
              <div className="stat-item">
                <div className="stat-value" style={{ color: "#1677ff" }}>{stats.benchCount ?? "-"}</div>
                <div className="stat-label">Bench</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{stats.applicationsSubmitted ?? "-"}</div>
                <div className="stat-label">Submitted</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{stats.hiredCount ?? "-"}</div>
                <div className="stat-label">Hired</div>
              </div>
              <div className="stat-item">
                <div className="stat-value" style={{ color: "#52c41a" }}>{stats.activeHires ?? "-"}</div>
                <div className="stat-label">Active</div>
              </div>
            </div>
          )}

          <div className="partner-actions">
            <Popconfirm
              title="Remove partner?"
              description="This will permanently remove this partner from your list."
              onConfirm={() => handleRemove(partner)}
              okText="Remove"
              okButtonProps={{ danger: true }}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={removingId === partner.id}
              >
                Remove
              </Button>
            </Popconfirm>
          </div>
        </PartnerCard>
      </Col>
    );
  };

  const renderInvitedPartnerCard = (partner) => (
    <Col xs={24} sm={12} lg={8} xl={6} key={partner.id}>
      <PartnerCard style={{ borderLeft: "3px solid #faad14" }}>
        <div className="partner-header">
          <Avatar size={44} icon={<UserOutlined />} style={{ background: "#fff7e6", color: "#faad14" }} />
          <div>
            <p className="partner-name">{partner.invitedName || "Unknown"}</p>
            <p className="partner-org">{partner.invitedEmail}</p>
          </div>
          <div style={{ marginLeft: "auto" }}>{getStatusBadge(partner)}</div>
        </div>

        <div className="partner-meta">
          {partner.invitedContact && (
            <span className="partner-meta-item">
              <PhoneOutlined style={{ color: "#999" }} />
              {partner.invitedContact}
            </span>
          )}
          {partner.invitedAt && (
            <span className="partner-meta-item">
              <MailOutlined style={{ color: "#999" }} />
              Invited {new Date(partner.invitedAt).toLocaleDateString("en-US", {
                day: "2-digit", month: "short", year: "numeric",
              })}
            </span>
          )}
        </div>

        <div className="partner-actions">
          <Tooltip title="Resend invitation email">
            <Button
              size="small"
              icon={<SendOutlined />}
              loading={resendingId === partner.id}
              onClick={() => handleResendInvite(partner)}
            >
              Resend
            </Button>
          </Tooltip>
          <Popconfirm
            title="Cancel invitation?"
            description="The invitation link will no longer work."
            onConfirm={() => handleCancelInvite(partner)}
            okText="Cancel Invite"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Cancel
            </Button>
          </Popconfirm>
        </div>
      </PartnerCard>
    </Col>
  );

  const tabItems = [
    {
      key: "active",
      label: (
        <span>
          <CheckCircleOutlined /> Active Partners ({activePartners.length})
        </span>
      ),
      children: (
        <Spin spinning={loading}>
          {activePartners.length === 0 && !loading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No active partners yet"
              style={{ padding: "60px 0" }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddModalVisible(true)}
                style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
              >
                Add Your First Partner
              </Button>
            </Empty>
          ) : (
            <Row gutter={[20, 20]} style={{ padding: "16px 4px 4px" }}>
              {activePartners.map(renderActivePartnerCard)}
            </Row>
          )}
        </Spin>
      ),
    },
    {
      key: "invited",
      label: (
        <span>
          <ClockCircleOutlined /> Invitations Pending ({invitedPartners.length})
        </span>
      ),
      children: (
        <Spin spinning={loading}>
          {invitedPartners.length === 0 && !loading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No pending invitations"
              style={{ padding: "60px 0" }}
            >
              <Button
                icon={<MailOutlined />}
                onClick={() => setAddModalVisible(true)}
              >
                Invite a Partner
              </Button>
            </Empty>
          ) : (
            <Row gutter={[20, 20]} style={{ padding: "16px 4px 4px" }}>
              {invitedPartners.map(renderInvitedPartnerCard)}
            </Row>
          )}
        </Spin>
      ),
    },
  ];

  return (
    <MyPartnersWrapper>
      <PageHeader>
        <h1>My Partners</h1>
        <p>Manage your partner network — add offline partners or invite new ones to the platform</p>
      </PageHeader>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <Space>
          <Space.Compact style={{ background: "#f8f9fd", padding: "8px 16px", borderRadius: 8, border: "1px solid #e8e8e8" }}>
            <span style={{ color: "#666", fontSize: 13, marginRight: 16 }}>
              <TeamOutlined style={{ marginRight: 6 }} />
              Total: <strong>{partners.length}</strong>
            </span>
            <span style={{ color: "#52c41a", fontSize: 13, marginRight: 16 }}>
              <CheckCircleOutlined style={{ marginRight: 6 }} />
              Active: <strong>{activePartners.length}</strong>
            </span>
            <span style={{ color: "#faad14", fontSize: 13 }}>
              <ClockCircleOutlined style={{ marginRight: 6 }} />
              Pending: <strong>{invitedPartners.length}</strong>
            </span>
          </Space.Compact>
        </Space>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalVisible(true)}
          style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
        >
          Add Partner
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ background: "#fff" }}
        tabBarStyle={{ padding: "4px 8px 0" }}
      />

      <AddPartnerModal
        visible={addModalVisible}
        onClose={() => setAddModalVisible(false)}
        onSuccess={fetchPartners}
      />
    </MyPartnersWrapper>
  );
};

export default MyPartners;
